import { mat3, vec3, mat4 } from 'gl-matrix';

/**
 * Convert translation vector to offset vector.
 * Translation vector represents the translation after rotation around center of rotation.
 * Offset vector is the vector in 4x4 matrix representation.
 *
 * These vectors are related by the formula:
 * offset = t + (I - R) * c
 * where:
 * t = translation vector
 * R = rotation matrix
 * c = center of rotation (usually treated as a parameter)
 * I = identity matrix
 *
 *
 * @param rotation
 * @param translate
 * @param centerOfRotation
 * @returns
 */
export function convertTranslationToOffset(
    rotation: mat3,
    translate: [number, number, number],
    centerOfRotation: [number, number, number]
): vec3 {
    const IminusR = mat3.create();
    mat3.identity(IminusR);
    mat3.subtract(IminusR, IminusR, rotation);

    const offset = vec3.create();
    vec3.transformMat3(offset, centerOfRotation, IminusR);
    offset[0] += translate[0];
    offset[1] += translate[1];
    offset[2] += translate[2];
    return offset;
}

/**
 * Create a 4x4 transformation matrix from rigid transform parameters.
 *
 * @param angles - rotation (Euler) angles [rx, ry, rz] in radians
 * @param translation - translation vector [tx, ty, tz]
 * @param centerOfRotation - center of rotation [cx, cy, cz]. Default is [0,0,0]
 * @returns 4x4 transformation matrix
 */

export function matrixFromRigidTransform(
    angles: vec3,
    translation: vec3,
    centerOfRotation: vec3 = vec3.fromValues(0, 0, 0)
): mat4 {
    const transform = mat4.create();
    mat4.rotateZ(transform, transform, angles[2]);
    mat4.rotateY(transform, transform, angles[1]);
    mat4.rotateX(transform, transform, angles[0]);

    const offset = convertTranslationToOffset(
        mat3.fromMat4(mat3.create(), transform),
        [translation[0], translation[1], translation[2]],
        [centerOfRotation[0], centerOfRotation[1], centerOfRotation[2]]
    );
    transform[12] = offset[0];
    transform[13] = offset[1];
    transform[14] = offset[2];
    transform[15] = 1;
    return transform;
}

/**
 * Decompose a 4x4 rigid transformation matrix into Euler angles (Z-Y-X convention)
 * and a translation vector. Inverse of matrixFromRigidTransform when center of
 * rotation is the origin.
 *
 * gl-matrix stores matrices in column-major order:
 *   [m0  m4  m8  m12]
 *   [m1  m5  m9  m13]
 *   [m2  m6  m10 m14]
 *   [m3  m7  m11 m15]
 */
export function decomposeRigidTransform(m: mat4): {
    translationMm: [number, number, number];
    eulerAnglesDeg: [number, number, number];
} {
    const translationMm: [number, number, number] = [m[12], m[13], m[14]];

    const sy = -m[8];
    const cy = Math.sqrt(m[0] * m[0] + m[4] * m[4]);

    let rx: number;
    let ry: number;
    let rz: number;

    if (cy > 1e-6) {
        rx = Math.atan2(m[9], m[10]);
        ry = Math.atan2(sy, cy);
        rz = Math.atan2(m[4], m[0]);
    } else {
        rx = Math.atan2(-m[6], m[5]);
        ry = Math.atan2(sy, cy);
        rz = 0;
    }

    const toDeg = 180 / Math.PI;
    const eulerAnglesDeg: [number, number, number] = [
        rx * toDeg,
        ry * toDeg,
        rz * toDeg,
    ];

    return { translationMm, eulerAnglesDeg };
}
